import React, { useContext, useState } from 'react';
import { useIntl } from 'react-intl';

import { EditorContextPanelContext } from '@confluence/editor-context-panel/entry-points/EditorContextPanelContext.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import {
	ANALYTICS_OPERATIONAL_SUBJECT,
	ANALYTICS_ORIGIN,
	ANALYTICS_UI_SUBJECT_ID,
} from '../../constants/snippetAnalytics.ts';
import { useSnippetAnalytics } from '../../hooks/analytics/useSnippetAnalytics.ts';
import { useInsertSnippet } from '../../hooks/panel/useInsertSnippet.ts';
import type { Snippet } from '../../types/snippet.ts';
import { SnippetPreview } from '../../preview/SnippetPreview.tsx';
import { SnippetPreviewSkeleton } from '../../preview/skeleton/SnippetPreviewSkeleton.tsx';
import { useSnippetsActions } from '../../store/snippetsStore.ts';
import { useSnippetPopupPreview } from '../preview/SnippetPopupPreviewContext.tsx';
import {
	SNIPPET_PREVIEW_CONTAINER,
	SnippetPreviewContainer,
} from '../preview/SnippetPreviewContainer.tsx';

import { SnippetPanelCard } from './SnippetPanelCard.tsx';
import { messages } from './SnippetPanelItem.i18n.ts';
import { SnippetPanelItemContent } from './SnippetPanelItemContent.tsx';

const EDITOR_PRE_SCALE_WIDTH = 800;

type SnippetPanelItemProps = {
	snippet: Snippet;
	contentId: string;
	spaceId: string;
	spaceKey: string;
};

export const SnippetPanelItem = ({
	snippet,
	contentId,
	spaceId,
	spaceKey,
}: SnippetPanelItemProps) => {
	const { userId } = useSessionData();
	const { editorAPI } = useContext(EditorContextPanelContext);
	const intl = useIntl();

	const [isHovered, setIsHovered] = useState(false);
	const [isMenuOpen, setIsMenuOpen] = useState(false);

	const insertSnippet = useInsertSnippet(editorAPI);
	const snippetsActions = useSnippetsActions();
	const { fireButtonClickedAnalytic, fireFailedAnalytic } = useSnippetAnalytics(
		ANALYTICS_ORIGIN.SNIPPET_PANEL,
	);
	const { showPreview, hidePreview } = useSnippetPopupPreview();

	const handleMouseEnter = (event: React.MouseEvent) => {
		setIsHovered(true);
		showPreview(snippet, event.currentTarget.getBoundingClientRect());
	};
	const handleMouseLeave = () => setIsHovered(false);
	const handleMenuOpenChange = (isOpen: boolean) => setIsMenuOpen(isOpen);
	const onClick = () => {
		const success = insertSnippet(snippet);
		if (success) {
			hidePreview();
			snippetsActions.recordSnippetInsert(snippet.snippetId);
			fireButtonClickedAnalytic(ANALYTICS_UI_SUBJECT_ID.INSERT_SNIPPET, {
				userId: userId ?? undefined,
				scope: snippet.scope,
				snippetId: snippet.snippetId,
				isShared: snippet.isShared,
				timestamp: Date.now(),
			});
		} else {
			fireFailedAnalytic(ANALYTICS_OPERATIONAL_SUBJECT.INSERT_SNIPPET, {
				userId: userId ?? undefined,
				scope: snippet.scope,
				snippetId: snippet.snippetId,
				isShared: snippet.isShared,
				timestamp: Date.now(),
			});
		}
	};

	const showActions = isHovered || snippet.isFavorite || isMenuOpen;
	const showPanelActions = isHovered || isMenuOpen;

	return (
		<SnippetPanelCard
			isActive={isMenuOpen}
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
			onClick={onClick}
			ariaLabel={intl.formatMessage(messages.snippetCardAriaLabel, { name: snippet.name })}
			testId={`snippet-card-button-${snippet.snippetId}`}
		>
			<SnippetPreviewContainer>
				<SnippetPreview
					snippet={snippet}
					dimensions={{
						width: `calc(${SNIPPET_PREVIEW_CONTAINER.width}px - (${SNIPPET_PREVIEW_CONTAINER.paddingInline} * 2))`,
						maxHeight: `calc(${SNIPPET_PREVIEW_CONTAINER.height}px - (${SNIPPET_PREVIEW_CONTAINER.paddingBlock} * 2))`,
					}}
					renderWidth={EDITOR_PRE_SCALE_WIDTH}
					loadingComponent={<SnippetPreviewSkeleton />}
				/>
			</SnippetPreviewContainer>
			<SnippetPanelItemContent
				snippet={snippet}
				isHovered={isHovered}
				showActions={showActions}
				showPanelActions={showPanelActions}
				onMenuOpenChange={handleMenuOpenChange}
				contentId={contentId}
				spaceId={spaceId}
				spaceKey={spaceKey}
			/>
		</SnippetPanelCard>
	);
};
